import axios from "axios";
import React, {
    ReactNode,
    createContext,
    useCallback,
    useContext,
    useMemo,
    useState,
} from "react";
import { Celebration } from "../calendar/ordinariate-calendar";
import { CalendarDay } from "../calendar/utils";
import {
    BibleTranslationSetting,
    PsalmCycleSetting,
    ReadingsSetting,
} from "./settings";

const NULL: any = null;

type DataDependency =
    | {
          type: "proper psalms";
          day: CalendarDay;
          psalmCycle: PsalmCycleSetting;
          time: "morning" | "evening";
      }
    | {
          type: "mattins readings";
          day: CalendarDay;
          mattinsReadings: ReadingsSetting;
          bibleTranslation: BibleTranslationSetting;
          refsOnly: boolean;
      }
    | {
          type: "evensong readings";
          day: CalendarDay;
          evensongReadings: ReadingsSetting;
          bibleTranslation: BibleTranslationSetting;
          refsOnly: boolean;
      }
    | {
          type: "ord readings";
          day: CalendarDay;
          celebration: Celebration;
          hour: "first-evensong" | "mattins" | "second-evensong";
          hymnIds: string[];
          benAntIds: string[];
          magAntIds: string[];
          invAntIds: string[];
          oneDayAheadPsalter: boolean;
      }
    | { type: "ord text list" }
    | { type: "special export" }
    | { type: "tenebrae"; day: "thursday" | "friday" | "saturday" };

export type LoadableDataDependency<T> =
    | { state: "loading" }
    | { state: "loaded"; value: T };

type DataDependencyResults = {
    [id: string]: LoadableDataDependency<any>;
};

const RemoteDataContext = createContext<{
    results: DataDependencyResults;
    addDependency: (dependency: DataDependency) => Promise<void>;
}>(NULL);

const getDependencyId = (dependency: DataDependency) => {
    return JSON.stringify(dependency);
};

export function useDataDependency<T>(
    dependency: DataDependency
): LoadableDataDependency<T> {
    const dependencyId = getDependencyId(dependency);

    const context = useContext(RemoteDataContext);

    console.log("use data dependency");

    if (context.results[dependencyId]) {
        console.log("already found");
        return context.results[dependencyId];
    } else {
        console.log("add dependency");
        context.addDependency(dependency);

        return { state: "loading" };
    }
}

export const RemoteDataProvider: React.FC<{ children: ReactNode }> = (
    props
) => {
    const [results, setResults] = useState<DataDependencyResults>({});

    const addDependency = useCallback(async (dependency: DataDependency) => {
        setResults((pr) => {
            return {
                ...pr,
                [getDependencyId(dependency)]: { state: "loading" },
            };
        });

        const response = await axios.post("/query", {
            dependency,
        });

        console.log("RESPONSE FROM EXPRESS", response);

        setResults((pr) => {
            return {
                ...pr,
                [getDependencyId(dependency)]: {
                    state: "loaded",
                    value: response.data,
                },
            };
        });
    }, []);

    return (
        <RemoteDataContext.Provider
            value={useMemo(() => {
                return { results, addDependency };
            }, [results, addDependency])}
        >
            {props.children}
        </RemoteDataContext.Provider>
    );
};
